/* Tagged-orders drill-down: click an event's "Orders tagged" count to see every
   shipment tagged to it (paginated via GET /kci/event/{id}/orders). Each AWB links
   to its order-detail page; a CSV export mirrors /event/{id}/export/awbs. */
import React, { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Chip, Typography, Tooltip,
} from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import { useSnackbar } from 'notistack'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import { apiConstants, getDownload } from '../../common/constants'
import { useEventOrdersQuery } from '../../redux/kciApiSlice'
import { NAVY, gridSx, CellLine, CellStack } from './kciUi'

const outlineBtnSx = { textTransform: 'none', fontWeight: 600, fontSize: 12.5, borderRadius: '8px', color: '#475569', borderColor: '#CBD5E1', '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' } }

export default function TaggedOrdersModal({ event, onClose }) {
  const { enqueueSnackbar } = useSnackbar()
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const { data, isFetching } = useEventOrdersQuery({ id: event.id, page: page + 1, size })
  const rows = data?.orders || []
  const total = data?.totalRecords ?? rows.length

  const openOrder = (awb) => { if (awb) window.open(`/order-detail/${awb}`, '_blank', 'noopener,noreferrer') }
  const doExport = async () => {
    try {
      const res = await getDownload(apiConstants.BASE_LOCAL + `kci/event/${event.id}/export/awbs`)
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a'); a.href = url; a.download = `kci_${event.id}_awbs.csv`
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url)
    } catch { enqueueSnackbar('Export failed.', { variant: 'error' }) }
  }

  const columns = [
    {
      field: 'order', headerName: 'AWB / Order', minWidth: 190, flex: 1.3, sortable: false,
      renderCell: (p) => (
        <CellStack>
          <Box onClick={() => openOrder(p.row.awbNumber)} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#1565c0', fontWeight: 600, fontSize: 12.5, cursor: p.row.awbNumber ? 'pointer' : 'default', '&:hover': { textDecoration: p.row.awbNumber ? 'underline' : 'none' } }}>
            {p.row.awbNumber || '—'}{p.row.awbNumber && <OpenInNewOutlinedIcon sx={{ fontSize: 13 }} />}
          </Box>
          <CellLine muted ellipsis>{p.row.orderReference || '—'}</CellLine>
        </CellStack>
      ),
    },
    {
      field: 'courier', headerName: 'Courier · Pincode', minWidth: 160, flex: 1, sortable: false,
      renderCell: (p) => <CellStack><CellLine bold>{p.row.courierCode || '—'}</CellLine><CellLine muted>{p.row.pincode || '—'}</CellLine></CellStack>,
    },
    { field: 'direction', headerName: 'Direction', minWidth: 110, sortable: false, renderCell: (p) => <Chip size='small' variant='outlined' label={p.row.direction || '—'} sx={{ fontSize: 11, fontWeight: 600 }} /> },
    { field: 'orderStatus', headerName: 'Status', minWidth: 140, flex: 1, sortable: false, renderCell: (p) => <Tooltip title={(p.row.orderStatus || '').replace(/_/g, ' ')}><CellLine ellipsis>{p.row.orderStatus || '—'}</CellLine></Tooltip> },
    { field: 'taggedAt', headerName: 'Tagged at', minWidth: 150, flex: 1, sortable: false, renderCell: (p) => <CellLine muted>{p.row.taggedAt ? new Date(p.row.taggedAt).toLocaleString('en-IN') : '—'}</CellLine> },
  ]

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ py: 1.75, fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 16, color: NAVY }}>
        Tagged orders — {event.title}
        <Typography sx={{ fontSize: 12, color: '#637381', fontWeight: 500, mt: 0.25 }}>{total.toLocaleString()} shipment{total === 1 ? '' : 's'} tagged to this event · click an AWB to open its order</Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#F7F8FA' }}>
        <Box sx={{ bgcolor: '#fff', border: '1px solid #E7EAF0', borderRadius: '10px', overflow: 'hidden' }}>
          <ProzoDataGrid
            columns={columns} rows={rows} getRowId={(r) => r.awbNumber || r.orderReference || Math.random()} rowCount={total}
            paginationMode='server' page={page} setPage={setPage} sizePerPage={size} setSizePerPage={setSize}
            loading={isFetching} getRowHeight={() => 'auto'} sx={gridSx}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
        <Button variant='outlined' sx={outlineBtnSx} startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />} onClick={doExport}>Download all (CSV)</Button>
        <Button variant='outlined' sx={outlineBtnSx} onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
